<template>
  <div class="filter-panel-wrapper">
    <v-expand-transition>
      <div
        v-if="show"
        class="filter-panel"
        data-product-fruits="ad-materials-filter-panel"
      >
        <div class="filter-header">
          <h3>{{ $t('ad-materials.filter.title') }}</h3>
          <v-btn
            icon
            variant="text"
            @click="closePanel"
            density="comfortable"
          >
            <v-icon class="text-grey-darken-2">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="filter-content">
          <!-- Target Audience -->
          <div class="filter-section">
            <div
              class="section-header"
              @click="toggleSection('target')"
            >
              <h4>{{ $t('ad-materials.filter.target.label') }}</h4>
              <v-icon>{{ expandedSections.target ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="expandedSections.target">
                <div class="checkbox-group">
                  <v-checkbox
                    v-for="(label, value) in targetOptions"
                    :key="value"
                    v-model="selectedTargets"
                    color="primary"
                    :label="label"
                    :value="value"
                    density="compact"
                  />
                </div>
              </div>
            </v-expand-transition>
          </div>

          <!-- Topic -->
          <div
            class="filter-section"
            v-if="topics.length"
          >
            <div
              class="section-header"
              @click="toggleSection('topic')"
            >
              <h4>{{ $t('ad-materials.filter.topic.label') }}</h4>
              <v-icon>{{ expandedSections.topic ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div
                v-show="expandedSections.topic"
                class="checkbox-group"
              >
                <v-checkbox
                  v-for="(topic, index) in topics"
                  :key="index"
                  v-model="selectedTopics"
                  color="primary"
                  :label="String(topic)"
                  :value="topic"
                  density="compact"
                />
              </div>
            </v-expand-transition>
          </div>

          <!-- Asset Type -->
          <div class="filter-section">
            <div
              class="section-header"
              @click="toggleSection('assetType')"
            >
              <h4>{{ $t('ad-materials.filter.asset-type.label') }}</h4>
              <v-icon>{{
                expandedSections.assetType ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="expandedSections.assetType">
                <v-radio-group
                  v-model="selectedAssetType"
                  density="compact"
                >
                  <v-radio
                    v-for="(label, value) in assetTypes"
                    :key="value"
                    color="primary"
                    :label="label"
                    :value="value"
                    density="compact"
                  />
                </v-radio-group>
              </div>
            </v-expand-transition>
          </div>

          <!-- Origin -->
          <div class="filter-section">
            <div
              class="section-header"
              @click="toggleSection('origin')"
            >
              <h4>{{ $t('ad-materials.filter.origin.label') }}</h4>
              <v-icon>{{ expandedSections.origin ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="expandedSections.origin">
                <v-radio-group
                  v-model="selectedOrigin"
                  density="compact"
                >
                  <v-radio
                    v-for="(label, value) in originTypes"
                    :key="value"
                    color="primary"
                    :label="label"
                    :value="value"
                    density="compact"
                  />
                </v-radio-group>
              </div>
            </v-expand-transition>
          </div>
        </div>

        <div class="filter-footer">
          <p class="info-text">{{ $t('ad-materials.filter.footer') }}</p>
          <v-btn
            block
            color="primary"
            variant="outlined"
            @click="resetFilters"
          >
            {{ $t('ad-materials.filter.reset') }}
          </v-btn>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  topics: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:show', 'filter-change'])

const selectedTargets = ref(['buyers', 'sellers'])
const selectedTopics = ref([])
const selectedOrigin = ref('')
const selectedAssetType = ref('')

const expandedSections = ref({
  target: true,
  topic: true,
  origin: true,
  assetType: true,
})

const targetOptions = {
  buyers: t('ad-materials.filter.target.options.buyers'),
  sellers: t('ad-materials.filter.target.options.sellers'),
}

const assetTypes = {
  '': t('ad-materials.filter.asset-type.options.all'),
  shopping_cart: t('ad-materials.filter.asset-type.options.shopping_cart'),
  checklist: t('ad-materials.filter.asset-type.options.checklist'),
  unknown: t('ad-materials.filter.asset-type.options.unknown'),
  ad: t('ad-materials.filter.asset-type.options.ad'),
  flyer_a5: t('ad-materials.filter.asset-type.options.flyer_a5'),
  flyer: t('ad-materials.filter.asset-type.options.flyer'),
  poster: t('ad-materials.filter.asset-type.options.poster'),
  banner: t('ad-materials.filter.asset-type.options.banner'),
  social_image: t('ad-materials.filter.asset-type.options.social_image'),
  social_story: t('ad-materials.filter.asset-type.options.social_story'),
  coaster: t('ad-materials.filter.asset-type.options.coaster'),
  postcard_blank: t('ad-materials.filter.asset-type.options.postcard_blank'),
  postcard_text: t('ad-materials.filter.asset-type.options.postcard_text'),
  newspaper: t('ad-materials.filter.asset-type.options.newspaper'),
  guide: t('ad-materials.filter.asset-type.options.guide'),
}

const originTypes = {
  '': t('ad-materials.filter.origin.options.all'),
  'ai-generated': t('ad-materials.filter.origin.options.ai-generated'),
  custom: t('ad-materials.filter.origin.options.custom'),
}

const toggleSection = (section: keyof typeof expandedSections.value) => {
  expandedSections.value[section] = !expandedSections.value[section]
}

const closePanel = () => {
  emit('update:show', false)
}

const resetFilters = () => {
  selectedTargets.value = ['buyers', 'sellers']
  selectedTopics.value = []
  selectedOrigin.value = ''
  selectedAssetType.value = ''
  emitFilterChange()
}

const emitFilterChange = () => {
  emit('filter-change', {
    target: selectedTargets.value,
    topics: selectedTopics.value,
    origin: selectedOrigin.value,
    assetType: selectedAssetType.value,
  })
}

watch(
  [selectedTargets, selectedTopics, selectedOrigin, selectedAssetType],
  () => emitFilterChange(),
  { deep: true, immediate: true }
)
</script>

<style scoped>
.filter-panel-wrapper {
  width: 100%;
  max-width: 300px;
  flex-shrink: 0;
}

.filter-panel {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 1px 0 8px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-right: 0;
  overflow: hidden;
}

.filter-header {
  padding: 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 16px 16px 16px;
  margin-right: -17px;
  padding-right: calc(16px + 17px);
}

.filter-section {
  margin-bottom: 16px;
  border: 1px solid rgb(var(--v-theme-on-surface-variant));
  border-radius: 8px;
  padding: 4px 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
}

.section-header h4 {
  margin: 0;
  font-weight: 500;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.filter-footer {
  padding: 16px;
  border-top: 1px solid #eee;
}

.info-text {
  font-size: 12px;
  color: #666;
  margin-bottom: 16px;
}

@media (max-width: 600px) {
  .filter-panel-wrapper {
    width: 100%;
  }
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.v-expand-transition-enter-from,
.v-expand-transition-leave-to {
  opacity: 0;
  width: 0;
}

:deep(.v-input__details) {
  display: none !important;
}
</style>
